import type { Container } from 'pixi.js';
import type { LevelId, WorldZone } from '@duck/contracts';
import { darken, lighten, mix, type ThemeArt } from '../theme-art';
import * as furniture from '../furniture';

/**
 * PER-ROOM DECORATOR REGISTRY (Walls + Rooms — set-dressing fan-out prep).
 *
 * Each of the ten office rooms owns ONE module file in this directory (`<zoneId>.ts`, with `meeting-room`
 * → `meeting-room.ts` etc.) that default-exports a single {@link RoomDecorator}: `decorate(ctx)`. The map
 * layer ({@link '../office-map'.createOfficeMap}) builds the floor/rug/label for every room, then looks
 * up that room's decorator here and calls it to add the room's CHARACTER (its furniture). This replaces
 * the old monolithic `drawZoneCharacter` switch, so ten room-design agents can later own one file each and
 * never touch a shared switch — zero merge conflicts.
 *
 * ── THE CONTRACT a room module implements ───────────────────────────────────────────────────────────
 *   import type { RoomDecorator } from './index';
 *   const decorate: RoomDecorator = (ctx) => { ...add furniture to ctx.container... };
 *   export default decorate;
 *
 * `decorate(ctx)` is called ONCE on map build/re-skin (never per frame) — so build, never animate, and
 * never allocate in a hot loop. Everything you add lives BELOW the ducks/labels in the engine z-order
 * (the map container), so room decoration can never occlude or dim the foreground (a11y floor, ADR-0012).
 */

/** A built furniture piece (a Pixi Container) the helpers return; the room positions + adds it. */
type Piece = Container;

/**
 * Everything a room module needs to dress its room, handed in so a module imports nothing but its own
 * `decorate` type. Add furniture to {@link container}; derive positions from {@link zone} (so a room
 * rides any future geometry move); theme via {@link art} (or the colour helpers); compose scenery from
 * {@link f} (the furniture catalog) — or draw raw Pixi if a room wants something bespoke.
 */
export interface RoomContext {
  /** The map container to add this room's decoration into (sits below the ducks/labels). */
  readonly container: Container;
  /** This room's authoritative rect + name + isolated flag (the single source of geometry). */
  readonly zone: WorldZone;
  /** The resolved theme art treatment (palette + materials) — the same one the whole map uses. */
  readonly art: ThemeArt;
  /** The furniture catalog (see {@link '../furniture'}): build a piece, set .x/.y, add it. */
  readonly f: typeof furniture;
  /** Colour helpers (so a room can tint a bespoke shape to the theme without an extra import). */
  readonly mix: typeof mix;
  readonly lighten: typeof lighten;
  readonly darken: typeof darken;
  /** Convenience: room centre + corners, precomputed from the zone (cuts boilerplate in every module). */
  readonly cx: number;
  readonly cy: number;
}

/** The function each room module default-exports: add this room's furniture to `ctx.container`. */
export type RoomDecorator = (ctx: RoomContext) => void;

/**
 * Helper a room module uses to place a built piece in one line: `place(ctx, f.sofa(art), x, y)`.
 *
 * Optional `kind` TAGS the piece as editable SCENERY (a room-registered kind) so the level editor can
 * capture it into data + render it from the scenery registry — making every bespoke furniture piece
 * movable/deletable, not just the shared catalog. Catalog pieces (f.X) already self-tag, so they don't
 * need a kind here; pass a kind only for room-local bespoke FURNITURE (not painted backdrop).
 */
export function place(ctx: RoomContext, piece: Piece, x: number, y: number, kind?: string): Piece {
  piece.x = x;
  piece.y = y;
  if (kind) (piece as unknown as { __decorKind?: string }).__decorKind = kind;
  ctx.container.addChild(piece);
  return piece;
}

// Phase 3 two-level refactor: BOTH levels' decorators are registered in ONE combined registry keyed by
// zone id. The OFFICE (default level) and the CASTLE (medieval_castle) zone ids are DISJOINT, so a single
// map never collides; `decorateRoom` no-ops any zone id without a decorator (safe by construction). The
// engine resolves the level from the synced `levelId` and renders whichever world's zones are present, so
// the same registry serves both — the office rooms below + the 27 castle rooms further down.
import ceoOffice from './ceo-office';
import kitchen from './kitchen';
import lounge from './lounge';
import meetingRoom from './meeting-room';
import openSpace from './open-space';
import serverRoom from './server-room';
import reception from './reception';
import studio from './studio';
import terrace from './terrace';
import gameRoom from './game-room';
import entryway from './entryway';
import toilet from './toilet';
// Castle Hearthhold 5x (ADR-0053): the active world's 27 rooms — each owns ONE decorator file in ./castle/.
import throneHall from './castle/throne-hall';
import grandCourt from './castle/grand-court';
import northGallery from './castle/north-gallery';
import westCloister from './castle/west-cloister';
import eastCloister from './castle/east-cloister';
import lowerBailey from './castle/lower-bailey';
import northRampart from './castle/north-rampart';
import watchTower from './castle/watch-tower';
import astronomyTower from './castle/astronomy-tower';
import armoury from './castle/armoury';
import barracks from './castle/barracks';
import trainingYard from './castle/training-yard';
import guardHall from './castle/guard-hall';
import chapel from './castle/chapel';
import warRoom from './castle/war-room';
import greatLibrary from './castle/great-library';
import scriptorium from './castle/scriptorium';
import alchemyLab from './castle/alchemy-lab';
import royalGardens from './castle/royal-gardens';
import orchard from './castle/orchard';
import mageTower from './castle/mage-tower';
import royalTower from './castle/royal-tower';
import dungeonJail from './castle/dungeon-jail';
import wineCellar from './castle/wine-cellar';
import gatehouse from './castle/gatehouse';
import treasuryVault from './castle/treasury-vault';
import catacombs from './castle/catacombs';
// Castle Hearthhold CLASSIC (medieval_office — the original 12-room v1). Its zone ids OVERLAP the 5x
// castle's (chapel, war-room, scriptorium, barracks, wine-cellar, gatehouse) + the office's (kitchen), so
// its decorators live under a SEPARATE per-level registry key. Aliased `mo*` to avoid import name clashes.
import moBarracks from './medieval-office/barracks';
import moChapel from './medieval-office/chapel';
import moGatehouse from './medieval-office/gatehouse';
import moHearthHall from './medieval-office/hearth-hall';
import moKitchen from './medieval-office/kitchen';
import moLibrary from './medieval-office/library';
import moScriptorium from './medieval-office/scriptorium';
import moSmithy from './medieval-office/smithy';
import moTavern from './medieval-office/tavern';
import moTreasureVault from './medieval-office/treasure-vault';
import moWarRoom from './medieval-office/war-room';
import moWineCellar from './medieval-office/wine-cellar';

/**
 * LEVEL-AWARE decorator registry: levelId → (zoneId → decorator). Keeping it per-level lets the two castles
 * (and the office) reuse the SAME room ids with DIFFERENT decorators (e.g. `chapel` exists in both castles).
 * Adding a room = add its file + one entry under the right level. A missing entry is a safe no-op.
 */
const ROOM_DECORATORS: Record<LevelId, Record<string, RoomDecorator>> = {
  // ── OFFICE (default level) ──
  office: {
    'ceo-office': ceoOffice,
    kitchen,
    lounge,
    'meeting-room': meetingRoom,
    'open-space': openSpace,
    'server-room': serverRoom,
    reception,
    studio,
    terrace,
    'game-room': gameRoom,
    entryway,
    toilet,
  },
  // ── CASTLE (medieval_castle level) — the 5x, 27 rooms ──
  medieval_castle: {
    'throne-hall': throneHall,
    'grand-court': grandCourt,
    'north-gallery': northGallery,
    'west-cloister': westCloister,
    'east-cloister': eastCloister,
    'lower-bailey': lowerBailey,
    'north-rampart': northRampart,
    'watch-tower': watchTower,
    'astronomy-tower': astronomyTower,
    armoury,
    barracks,
    'training-yard': trainingYard,
    'guard-hall': guardHall,
    chapel,
    'war-room': warRoom,
    'great-library': greatLibrary,
    scriptorium,
    'alchemy-lab': alchemyLab,
    'royal-gardens': royalGardens,
    orchard,
    'mage-tower': mageTower,
    'royal-tower': royalTower,
    'dungeon-jail': dungeonJail,
    'wine-cellar': wineCellar,
    gatehouse,
    'treasury-vault': treasuryVault,
    catacombs,
  },
  // ── CASTLE CLASSIC (medieval_office level) — the original 12 rooms ──
  medieval_office: {
    library: moLibrary,
    smithy: moSmithy,
    scriptorium: moScriptorium,
    'war-room': moWarRoom,
    chapel: moChapel,
    'hearth-hall': moHearthHall,
    kitchen: moKitchen,
    tavern: moTavern,
    barracks: moBarracks,
    'wine-cellar': moWineCellar,
    'treasure-vault': moTreasureVault,
    gatehouse: moGatehouse,
  },
};

/**
 * Dispatch a room's decorator (the registry replacement for the old `drawZoneCharacter` switch). Builds
 * the {@link RoomContext} once and calls the matching module; a zone with no module is a no-op (the floor
 * + label still render), so adding a room can never crash the map.
 */
export function decorateRoom(container: Container, zone: WorldZone, art: ThemeArt, levelId: LevelId): void {
  const decorate = ROOM_DECORATORS[levelId]?.[zone.id];
  if (!decorate) return;
  decorate({
    container,
    zone,
    art,
    f: furniture,
    mix,
    lighten,
    darken,
    cx: zone.x + zone.width / 2,
    cy: zone.y + zone.height / 2,
  });
}
